/**
 * Session Storage Utilities
 *
 * Web-only session storage for temporary auth flow data (PKCE code verifier, CSRF tokens).
 * These values are only needed during the OAuth flow and should not persist across sessions.
 */

import { Platform } from "react-native";
import { useSessionStore } from "@src/stores/session-store";

export const SESSION_KEYS = {
  CODE_VERIFIER: "auth_code_verifier",
  CSRF_TOKEN: "auth_csrf_token",
  NONCE: "auth_nonce",
  SESSION_UNLOCKED: "auth_session_unlocked",
} as const;

function isWebWithSessionStorage(): boolean {
  return Platform.OS === "web" && typeof window !== "undefined" && !!window.sessionStorage;
}

export const authSessionStorage = {
  get(key: string): string | null {
    if (!isWebWithSessionStorage()) return null;
    return window.sessionStorage.getItem(key);
  },

  set(key: string, value: string): void {
    if (!isWebWithSessionStorage()) return;
    window.sessionStorage.setItem(key, value);
  },

  remove(key: string): void {
    if (!isWebWithSessionStorage()) return;
    window.sessionStorage.removeItem(key);
  },

  clearAuthData(): void {
    this.remove(SESSION_KEYS.CODE_VERIFIER);
    this.remove(SESSION_KEYS.CSRF_TOKEN);
    this.remove(SESSION_KEYS.NONCE);
  },
};

/**
 * Session unlock tracking
 * Tracks whether the user has verified their identity in the current session.
 * - Web: Uses sessionStorage (survives page reloads, clears when browser tab closes)
 * - Native: Uses Zustand store (resets when app is killed/restarted)
 */
export function isSessionUnlocked(): boolean {
  if (isWebWithSessionStorage()) {
    return window.sessionStorage.getItem(SESSION_KEYS.SESSION_UNLOCKED) === "true";
  }
  return useSessionStore.getState().isUnlocked;
}

export function setSessionUnlocked(unlocked: boolean): void {
  if (isWebWithSessionStorage()) {
    if (unlocked) {
      window.sessionStorage.setItem(SESSION_KEYS.SESSION_UNLOCKED, "true");
    } else {
      window.sessionStorage.removeItem(SESSION_KEYS.SESSION_UNLOCKED);
    }
  }
  useSessionStore.getState().setUnlocked(unlocked);
}
